import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { isEqual } from "lodash";

import { GlobalSettings, OrderSettings } from "../../../../../services";
import { useTypedDispatch, useTypedSelector } from "../../../../../redux/store";
import order from "../../../../../redux/reducers/settings/order";
import sounds, {
	DEFAULT_ONLINE_ORDER_VOLUME,
	normalizeSounds,
	SoundSettingValue,
	SoundsState,
} from "../../../../../redux/reducers/settings/sounds";
import { useSnapshot } from "../../../../../hooks";
import { SettingsTabProps } from "../../../../../components/Settings/tabProps";
import { useNotification } from "../../../../../components/NotificationProvider";
import CustomTooltip from "../../../../../components/CustomTooltip";
import CustomWhisper from "../../../../../components/CustomWhisper";
import SoundSetting from "../../../../../components/SoundSetting";
import {
	Divider,
	StyledColumn,
	StyledSpan,
} from "../../../../../components/common";
import {
	DEFAULT_CALL_SOUND_URL,
	DEFAULT_ONLINE_ORDER_SOUND_URL,
	preloadCallSounds,
} from "../../../../../utils/notificationSound";

const tPath = "settings.interface.sounds";

const titleStyle: React.CSSProperties = { fontWeight: 600 };

const hintStyle: React.CSSProperties = {
	width: 320,
	whiteSpace: "normal",
};

const disabledContentStyle: React.CSSProperties = {
	opacity: 0.5,
	filter: "grayscale(1)",
	// The wrapper shows the hint: disabled buttons swallow mouse events.
	pointerEvents: "none",
};

interface SoundsSnapshot extends SoundsState {
	onlineOrder: SoundSettingValue;
}

/**
 * Writes to the same order settings row the notifier reads (the first one).
 * `active` stays untouched: it is switched in the order settings themselves.
 */
async function saveOnlineOrderSound(next: SoundSettingValue) {
	const [row] = (await OrderSettings.getAll()) ?? [];
	if (!row) return { ok: false, settings: null };

	const { general } = row.settings;
	await OrderSettings.update({
		...row,
		settings: {
			...row.settings,
			general: {
				...general,
				notifyOnlineOrders: { ...general.notifyOnlineOrders, ...next },
			},
		},
	});

	// OrderSettings.update does not report failures, so verify the saved result.
	const [saved] = (await OrderSettings.getAll()) ?? [];
	const notify = saved?.settings?.general?.notifyOnlineOrders;

	return {
		ok:
			notify?.volume === next.volume &&
			isEqual(notify?.sound, next.sound),
		settings: saved?.settings ?? null,
	};
}

async function saveCallSounds(next: SoundsState) {
	const globalSettings = await GlobalSettings.get();
	globalSettings.ui.sounds = next;
	await GlobalSettings.update(globalSettings);
}

const SoundsSettings: React.FC<SettingsTabProps> = ({
	methods,
	onHasChanges,
}) => {
	const { t } = useTranslation();
	const dispatch = useTypedDispatch();
	const { error: showError } = useNotification();

	const callSounds = useTypedSelector((state) => state.settings.sounds);
	const notify = useTypedSelector(
		(state) => state.settings.order?.general?.notifyOnlineOrders,
	);

	const onlineOrderActive = Boolean(notify?.active);

	const state = useMemo<SoundsSnapshot>(
		() => ({
			...normalizeSounds(callSounds),
			onlineOrder: {
				volume: notify?.volume ?? DEFAULT_ONLINE_ORDER_VOLUME,
				sound: notify?.sound ?? null,
			},
		}),
		[callSounds, notify?.sound, notify?.volume],
	);

	const [snapshot, setSnapshot] = useSnapshot<SoundsSnapshot>({
		state,
		methods,
		onHasChanges,
		async onSave({ onlineOrder, ...calls }) {
			const { onlineOrder: savedOnlineOrder, ...savedCalls } = state;
			let ok = true;
			let orderSettings: OrderSettings.Model["settings"] | null = null;
			let callsSaved = false;

			if (!isEqual(onlineOrder, savedOnlineOrder)) {
				const result = await saveOnlineOrderSound(onlineOrder);
				orderSettings = result.settings;
				ok = result.ok;
			}

			if (!isEqual(calls, savedCalls)) {
				try {
					await saveCallSounds(calls);
					callsSaved = true;
				} catch (e) {
					// eslint-disable-next-line no-console
					console.warn("[SoundsSettings] save failed", e);
					ok = false;
				}
			}

			// Update the store at once, so Save does not blink between the parts.
			if (orderSettings) dispatch(order.actions.set(orderSettings));
			if (callsSaved) {
				dispatch(sounds.actions.setAll(calls));
				preloadCallSounds(calls);
			}

			if (!ok) {
				showError(
					t("notification.title.error") ?? "Error",
					t(`${tPath}.saveError`) ?? "",
				);
				onHasChanges(true);
			}
		},
		onResetDefaults() {
			const defaults = sounds.getInitialState();
			return {
				...defaults,
				// A disabled block cannot be changed, not even by a reset.
				onlineOrder: onlineOrderActive
					? { volume: DEFAULT_ONLINE_ORDER_VOLUME, sound: null }
					: state.onlineOrder,
			};
		},
	});

	// useSnapshot ignores external changes (order settings, another dispatcher):
	// refresh the draft unless it has its own edits.
	const previousState = useRef(state);
	useEffect(() => {
		const previous = previousState.current;
		previousState.current = state;
		setSnapshot((current) =>
			isEqual(current, previous) ? state : current,
		);
	}, [setSnapshot, state]);

	// The parent clears the flag after "By default" and useSnapshot reports only
	// value changes, so without this the Save button would stay disabled.
	useEffect(() => {
		onHasChanges(!isEqual(state, snapshot));
	}, [onHasChanges, snapshot, state]);

	const onlineOrderHint = t(`${tPath}.onlineOrder.disabledHint`, {
		tab: t("settings.orders.tabs.orders.tabsConstants.main"),
	});

	return (
		<StyledColumn gap="24px" p="16px">
			<CustomWhisper
				speaker={
					onlineOrderActive ? undefined : (
						<CustomTooltip>
							<div style={hintStyle}>{onlineOrderHint}</div>
						</CustomTooltip>
					)
				}
			>
				<StyledColumn
					gap="10px"
					style={onlineOrderActive ? undefined : disabledContentStyle}
				>
					<StyledSpan style={titleStyle}>
						{t(`${tPath}.onlineOrder.title`) ?? ""}
					</StyledSpan>
					<SoundSetting
						value={snapshot.onlineOrder}
						onChange={(onlineOrder) =>
							setSnapshot((prev) => ({
								...prev,
								onlineOrder,
							}))
						}
						defaultSoundUrl={DEFAULT_ONLINE_ORDER_SOUND_URL}
						disabled={!onlineOrderActive}
					/>
				</StyledColumn>
			</CustomWhisper>

			<Divider side="top" />

			<StyledColumn gap="10px">
				<StyledSpan style={titleStyle}>
					{t(`${tPath}.incomingCall.title`) ?? ""}
				</StyledSpan>
				<SoundSetting
					value={snapshot.incomingCall}
					onChange={(incomingCall) =>
						setSnapshot((prev) => ({ ...prev, incomingCall }))
					}
					defaultSoundUrl={DEFAULT_CALL_SOUND_URL}
				/>
			</StyledColumn>

			<StyledColumn gap="10px">
				<StyledSpan style={titleStyle}>
					{t(`${tPath}.incomingCallDuringConversation.title`) ?? ""}
				</StyledSpan>
				<SoundSetting
					value={snapshot.incomingCallDuringConversation}
					onChange={(incomingCallDuringConversation) =>
						setSnapshot((prev) => ({
							...prev,
							incomingCallDuringConversation,
						}))
					}
					defaultSoundUrl={DEFAULT_CALL_SOUND_URL}
				/>
			</StyledColumn>
		</StyledColumn>
	);
};

export default SoundsSettings;
